/**
 * Create Manual Bill Page
 * Form-based UI: form fields, searchable vendor dropdown, line items added via modal only (no default line item).
 * Includes TDS column and TDS in totals.
 */

import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import {
  gstAppliesToDocumentFor,
  isPreGstRegistrationFor,
} from "../../../../shared/utils/gstApplicability";
import { GstPreRegistrationNotice } from "../../../../shared/presentation/components/GstPreRegistrationNotice";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { LocationPicker } from "../../../../shared/presentation/components/LocationPicker";
import { useActiveLocations } from "../../../../shared/hooks/useActiveLocations";
import { Breadcrumbs } from "../../../../ui/components/Breadcrumbs";
import { TextField } from "../../../../ui/components/TextField";
import {
  SearchableDropdown,
  ErrorBanner,
  useToast,
} from "../../../../shared/presentation/components";
import { getCompany } from "../../../../shared/data/storage/authStorage";
import {
  isDocumentFxMismatch,
  parseExchangeRateInputForPayload,
  resolveCurrencyFieldsFromCatalog,
} from "../../../../shared/utils/manualDocumentCurrency";
import { isExchangeRateMissingForTallyPost } from "../../../../shared/utils/documentExchangeRate";
import { useZohoDocumentExchangeRate } from "../../../../shared/hooks/useZohoDocumentExchangeRate";
import {
  formatReverseChargeAmountWithDocument,
  resolveReverseChargeAmount,
  shouldShowReverseChargeMessage,
} from "../../utils/reverseCharge";
import { buildManualBillJson } from "../../../../shared/utils/buildManualDocumentJson";
import { fetchCurrencies, type Currency } from "../../../../services/api";
import { handleError } from "../../../../utils/errorHandling";
import { useTaxes } from "../../../../shared/presentation/hooks/useTaxes";
import { usePermissions } from "../../../../shared/presentation/hooks/usePermissions";
import {
  MODULE_CODES,
  ACTION_CODES,
} from "../../../../shared/constants/permissions";
import { ReviewRequestModal } from "../../../../shared/presentation/components/ReviewRequestModal";
import { createManualBillDraft } from "../../data/api/billsApi";
import {
  BillDataEntity,
  LineItemEntity,
} from "../../domain/entities/BillEntity";
import { CreateManualBillUseCase } from "../../domain/use-cases/bills/CreateManualBillUseCase";
import { BillRepositoryImpl } from "../../data/repositories/BillRepositoryImpl";
import EditLineItemManualBill from "../components/EditLineItemManualBill";
import SearchPartyModal from "../components/SearchPartyModal";
import { getAccountNameLeaf, getStateAbbreviation } from "../../../../utils";
import { getDocumentNumberValidationWarning } from "../../../../shared/utils/documentNumberValidation";
import { extractErrorMessage } from "../../../../shared/utils/errorUtils";
import { formatDetectedLineItemGstDisplay } from "../../../../shared/utils/lineItemGstDisplay";
import {
  fetchTcsList,
  mapTcsDTOToEntity,
} from "../../../../shared/data/api/tcsApi";
import type { TcsEntity } from "../../../../shared/domain/entities/TaxEntity";
import {
  FeatherPlus,
  FeatherX,
  FeatherEdit2,
  FeatherChevronDown,
  FeatherInfo,
  FeatherUserPlus,
} from "@subframe/core";
import {
  buildManualBillVendorPatch,
  CREATE_MANUAL_BILL_ROUTE,
  type CreateManualBillNavigationState,
  type VendorLikeForManualBill,
} from "../utils/manualBillVendorPatch";
import {
  getCreateAndPushLabel,
  getStoredAccountingBackend,
} from "../../../../config/accounting";

const billRepository = new BillRepositoryImpl();
const createManualBillUseCase = new CreateManualBillUseCase(billRepository);

const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

function defaultManualBillData(): BillDataEntity {
  const today = new Date().toISOString().slice(0, 10);
  const due = new Date();
  due.setDate(due.getDate() + 30);
  const dueDate = due.toISOString().slice(0, 10);

  return {
    vendor_id: "",
    vendor_name: "",
    vendor_gstin: "",
    bill_number: "",
    invoice_number: "",
    reference_number: "",
    date: today,
    invoice_date: today,
    due_date: dueDate,
    currency_id: "",
    currency_code: "INR",
    currency_symbol: "₹",
    customer_name: "",
    customer_gstin: "",
    gst_no: "",
    gst_treatment: "",
    source_of_supply: "",
    destination_of_supply: "",
    place_of_supply: "",
    eway_bill_number: "",
    irn_number: "",
    line_items: [],
    sub_total: 0,
    tax_total: 0,
    total: 0,
    discount: 0,
    payment_terms: 0,
    payment_terms_label: "",
    is_item_level_tax_calc: true,
    is_inclusive_tax: false,
    notes: "",
    terms: "",
    tcs_tax_id: "",
    tcs_tax_name: "",
    tcs_tax_percentage: undefined,
    tcs_nature_of_collection: "",
  };
}

const NO_TCS_ENTITY: TcsEntity = {
  id: 0,
  tcs_id: "",
  tcs_tax_name: "No TCS",
  tcs_tax_percentage: 0,
};

const CreateManualBillPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useToast();
  const { getPermissionInfo, loading: permissionsLoading } = usePermissions();
  const company = getCompany();
  const accountingBackend = getStoredAccountingBackend();
  const isTallyBackend = accountingBackend === "tally";
  const createAndPushLabel = getCreateAndPushLabel(accountingBackend);
  const companyGstEnabled = company?.companyGstEnabled ?? 0;
  const createPermissionInfo = getPermissionInfo(
    MODULE_CODES.BILL,
    ACTION_CODES.CREATE,
  );
  const canCreate = createPermissionInfo.granted;
  const requiresReviewForCreate = createPermissionInfo.requiresReview;

  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [draftIdForReview, setDraftIdForReview] = useState<number | null>(null);
  const [changeDetailsForReview, setChangeDetailsForReview] = useState<{
    raw_json: BillDataEntity;
    action_types: string[];
  } | null>(null);

  const [billData, setBillData] = useState<BillDataEntity>(
    defaultManualBillData,
  );

  // ZOUR-19: does GST apply to the bill BEING TYPED?
  //
  // Derived from `billData.date` rather than computed once from the company,
  // so it re-evaluates on every keystroke in the date field — a user who
  // back-dates a bill past the registration date sees the tax columns and the
  // ITC picker disappear as they do it, instead of finding out at push time.
  //
  // This is the reason the browser resolves this at all rather than asking the
  // server: there is no round trip that keeps up with a date input.
  //
  // ZOUR-92: and on the registration picked above it, which can have a later
  // date of its own — so switching the Location picker re-draws the form just
  // as editing the date does.
  const isGstEnabled = gstAppliesToDocumentFor(
    billData.date || billData.invoice_date || "",
    billData.location_id ?? null,
    company
  );
  const showPreGstNotice = isPreGstRegistrationFor(
    billData.date || billData.invoice_date || "",
    billData.location_id ?? null,
    company
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSearchPartyModalOpen, setIsSearchPartyModalOpen] = useState(false);
  const [isEditLineItemModalOpen, setIsEditLineItemModalOpen] = useState(false);
  const [editingLineItemData, setEditingLineItemData] =
    useState<LineItemEntity | null>(null);
  const [editingLineItemIndex, setEditingLineItemIndex] = useState<
    number | null
  >(null);
  const [originalAccountName, setOriginalAccountName] = useState("");
  const topRef = useRef<HTMLDivElement>(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [currenciesError, setCurrenciesError] = useState<string | null>(null);
  const [exchangeRateInput, setExchangeRateInput] = useState("");

  const { taxes } = useTaxes();

  const showExchangeRateField = useMemo(
    () => isDocumentFxMismatch(billData.currency_code),
    [billData.currency_code],
  );
  /** Tally cannot look up a rate, so a foreign-currency bill must carry one. */
  const exchangeRateRequired = isTallyBackend && showExchangeRateField;

  const { loading: exchangeRateLoading, error: exchangeRateFetchError } =
    useZohoDocumentExchangeRate({
      enabled: !isTallyBackend && showExchangeRateField,
      currencyId: billData.currency_id,
      fromDate: billData.date || billData.invoice_date,
      currentRate: exchangeRateInput,
      onRateFetched: (rate) => {
        setExchangeRateInput(String(rate));
        setErrors((prev) => {
          if (!prev.exchange_rate) return prev;
          const next = { ...prev };
          delete next.exchange_rate;
          return next;
        });
      },
    });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setCurrenciesLoading(true);
        setCurrenciesError(null);
        const res = await fetchCurrencies();
        if (!cancelled) setCurrencies(res.data);
      } catch (e) {
        if (!cancelled)
          setCurrenciesError(handleError(e, "currencies").message);
      } finally {
        if (!cancelled) setCurrenciesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const id = billData.currency_id?.trim();
    if (!id || currencies.length === 0) return;
    const resolved = resolveCurrencyFieldsFromCatalog(currencies, id);
    if (!resolved) return;
    if (
      resolved.currency_code === billData.currency_code &&
      resolved.currency_symbol === billData.currency_symbol
    ) {
      return;
    }
    setBillData((prev) => ({ ...prev, ...resolved }));
  }, [
    currencies,
    billData.currency_id,
    billData.currency_code,
    billData.currency_symbol,
  ]);

  useEffect(() => {
    if (!currencies.length || billData.currency_id?.trim()) return;
    const code = billData.currency_code?.trim();
    if (!code) return;
    const hit = currencies.find((c) => c.currency_code === code);
    if (hit) {
      setBillData((prev) => ({
        ...prev,
        currency_id: hit.currency_id,
        currency_symbol: hit.currency_symbol || prev.currency_symbol,
      }));
    }
  }, [currencies, billData.currency_id, billData.currency_code]);

  const hasPrefilledCompanyGstin = useRef(false);
  // ZOUR-65: which of our GST registrations receives this bill.
  const {
    locations: activeLocations,
    hasMultiple: hasMultipleLocations,
    primary: primaryLocation,
  } = useActiveLocations();

  const selectedLocation = useMemo(
    () =>
      activeLocations.find(
        (loc) => loc.zoho_location_id === billData.location_id
      ) ??
      primaryLocation ??
      null,
    [activeLocations, billData.location_id, primaryLocation]
  );

  /**
   * ZOUR-65 — our GSTIN and the destination of supply come from the LOCATION.
   *
   * A bill addressed to the Bengaluru branch is received in Karnataka by the
   * Bengaluru GSTIN, not by the company's home registration. Compare on
   * `state_code`, never `state`: a synced row holds "Karnataka" in `state`.
   *
   * Re-runs when the picker changes — switching the location has to move both
   * fields, or the reviewer sees one branch's GSTIN under another's name. The
   * company profile stays the fallback, and on a single-registration tenant the
   * synthetic primary IS the company profile, so nothing changes for them.
   */
  useEffect(() => {
    const locationGstin = selectedLocation?.gstin ?? null;
    const gst = locationGstin ?? company?.gstNumber ?? "";
    if (!gst) return;

    const locationChanged = hasPrefilledCompanyGstin.current && !!locationGstin;
    if (hasPrefilledCompanyGstin.current && !locationChanged) return;
    hasPrefilledCompanyGstin.current = true;

    const destState =
      selectedLocation?.state_code ||
      getStateAbbreviation(company?.state || "") ||
      "";

    setBillData((prev) => ({
      ...prev,
      customer_gstin: gst,
      destination_of_supply: destState || prev.destination_of_supply,
    }));
  }, [
    company?.gstNumber,
    company?.state,
    selectedLocation?.gstin,
    selectedLocation?.state_code,
  ]);

  // Default to the primary registration so the bill always carries one.
  useEffect(() => {
    if (billData.location_id || !primaryLocation?.zoho_location_id) return;
    setBillData((prev) => ({
      ...prev,
      location_id: primaryLocation.zoho_location_id,
    }));
  }, [primaryLocation?.zoho_location_id, billData.location_id]);

  const updateBillData = useCallback((updates: Partial<BillDataEntity>) => {
    setBillData((prev) => ({ ...prev, ...updates }));
  }, []);

  const lineItems = useMemo(
    () => billData.line_items || [],
    [billData.line_items],
  );

  /**
   * ZOUR-65 — moving a manual bill to another registration CLEARS its lines.
   *
   * The tax family on every line (IGST vs CGST+SGST) is decided by the
   * registration that receives the bill, and nothing re-derives it here: a
   * manual bill has no processing job, so the re-run that fixes this on an
   * uploaded bill does not exist. Keeping the lines would produce the defect
   * found in UAT — TN tax sitting under a KA registration — and remapping each
   * line's tax id is a guess about rates we were never told. Clearing says what
   * happened and asks for the two or three lines again; the user chose it
   * ("clearing is the safe pick", 2026-09-16).
   *
   * Silent when there is nothing to lose, which is the normal case: the picker
   * is usually set before any line exists.
   */
  const [locationClearedNote, setLocationClearedNote] = useState<string | null>(
    null,
  );

  const handleLocationChange = useCallback(
    (locationId: string) => {
      setBillData((prev) => {
        if ((prev.location_id ?? null) === locationId) return prev;

        const hadLines = (prev.line_items || []).length > 0;
        if (!hadLines) {
          setLocationClearedNote(null);
          return { ...prev, location_id: locationId };
        }

        const name =
          activeLocations.find(
            (candidate) => candidate.zoho_location_id === locationId,
          )?.name ?? "the new registration";

        setLocationClearedNote(
          `Registration changed to ${name}. Line items were cleared because the tax type (IGST vs CGST+SGST) depends on the registration — add them again.`,
        );

        return {
          ...prev,
          location_id: locationId,
          line_items: [],
          sub_total: 0,
          tax_total: 0,
          total: 0,
          discount: 0,
        };
      });
    },
    [activeLocations],
  );

  const hasSelectedVendor = Boolean(billData.vendor_id?.trim());
  const vendorNameDisplayText = !hasSelectedVendor
    ? "No vendor selected"
    : billData.vendor_name?.trim() || "";
  const vendorAddressDisplayText = !hasSelectedVendor
    ? "No vendor selected"
    : billData.vendor_address?.trim()
      ? billData.vendor_address.trim()
      : "Vendor has no address";
  const discount = billData.discount ?? 0;

  const formatAmount = (amount: number, _fallback = "0.00"): string =>
    (Number(amount) ?? 0).toFixed(2);
  const parseTaxRateFromName = (taxName: string): number | null => {
    const match = taxName?.match(/([\d.]+)/);
    return match ? parseFloat(match[1]) : null;
  };
  const formatRate = (rate: number): string => {
    const fixed = Number(rate.toFixed(2));
    return Number.isInteger(fixed) ? fixed.toString() : fixed.toString();
  };
  const calculateTaxBreakdown = (
    items: LineItemEntity[],
    data?: BillDataEntity | null,
  ): { rows: { label: string; amount: number }[]; totalTax: number } => {
    const taxGroups: Record<
      string,
      { type: "IGST" | "GST"; rate: number; amount: number }
    > = {};
    const normalizeState = (state: string | null | undefined): string => {
      if (!state) return "";
      const normalized = state.toUpperCase().trim();
      if (normalized.length === 2) return normalized;
      try {
        return getStateAbbreviation(state) || normalized;
      } catch {
        return normalized;
      }
    };
    const placeOfSupply = normalizeState(data?.place_of_supply);
    const destinationOfSupply = normalizeState(data?.destination_of_supply);
    const hasSupplyInfo = placeOfSupply && destinationOfSupply;
    const isInterState = hasSupplyInfo && placeOfSupply !== destinationOfSupply;
    items.forEach((item) => {
      const taxName = (item.tax_name || "").toString();
      const taxPercentageRaw = item.tax_percentage;
      const taxRateFromPercentage =
        taxPercentageRaw !== undefined &&
        taxPercentageRaw !== null &&
        !isNaN(Number(taxPercentageRaw))
          ? parseFloat(String(taxPercentageRaw))
          : null;
      const parsedRateFromName = parseTaxRateFromName(taxName);
      const taxRate =
        taxRateFromPercentage !== null
          ? taxRateFromPercentage
          : parsedRateFromName !== null && !isNaN(parsedRateFromName)
            ? parsedRateFromName
            : 0;
      if (!taxRate) return;
      let taxType: "IGST" | "GST";
      const taxNameUpper = taxName.toUpperCase();
      if (taxNameUpper.includes("IGST")) taxType = "IGST";
      else if (taxNameUpper.includes("GST")) taxType = "GST";
      else if (hasSupplyInfo) taxType = isInterState ? "IGST" : "GST";
      else taxType = "GST";
      const taxableAmount = (item.rate || 0) * (item.quantity || 0);
      const taxAmount = (taxableAmount * taxRate) / 100;
      const key = `${taxType}-${taxRate}`;
      if (!taxGroups[key])
        taxGroups[key] = { type: taxType, rate: taxRate, amount: 0 };
      taxGroups[key].amount += taxAmount;
    });
    const rows: { label: string; amount: number }[] = [];
    Object.values(taxGroups).forEach((group) => {
      if (group.type === "IGST") {
        rows.push({
          label: `IGST ${formatRate(group.rate)}%`,
          amount: group.amount,
        });
      } else {
        const halfRate = group.rate / 2;
        const halfAmount = group.amount / 2;
        rows.push(
          { label: `SGST ${formatRate(halfRate)}%`, amount: halfAmount },
          { label: `CGST ${formatRate(halfRate)}%`, amount: halfAmount },
        );
      }
    });
    const totalTax = Object.values(taxGroups).reduce(
      (sum, g) => sum + g.amount,
      0,
    );
    return { rows, totalTax };
  };
  const taxBreakdown = useMemo(() => {
    return calculateTaxBreakdown(lineItems, billData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, billData?.place_of_supply, billData?.destination_of_supply]);

  const isInterState = useMemo(() => {
    const sos = (billData?.source_of_supply ?? billData?.place_of_supply ?? "")
      .toString()
      .trim()
      .toUpperCase();
    const dos = (billData?.destination_of_supply ?? "")
      .toString()
      .trim()
      .toUpperCase();
    const sosNorm =
      sos.length === 2
        ? sos
        : getStateAbbreviation(
            billData?.source_of_supply ?? billData?.place_of_supply ?? "",
          ) || sos;
    const dosNorm =
      dos.length === 2
        ? dos
        : getStateAbbreviation(billData?.destination_of_supply ?? "") || dos;
    return !!sosNorm && !!dosNorm && sosNorm !== dosNorm;
  }, [
    billData?.source_of_supply,
    billData?.place_of_supply,
    billData?.destination_of_supply,
  ]);

  const totalTDS = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const taxable = (item.rate ?? 0) * (item.quantity ?? 0);
      const pct = item.tds_tax_percentage ?? 0;
      return sum + (taxable * pct) / 100;
    }, 0);
  }, [lineItems]);

  const hasAnyTds = useMemo(
    () =>
      lineItems.some(
        (item) =>
          !!(
            item.tds_tax_id &&
            item.tds_tax_id !== "" &&
            (item.tds_tax_percentage ?? 0) > 0
          ),
      ),
    [lineItems],
  );

  /** Overseas / RCM vendor with a taxed line: GST is self-assessed, not payable to the vendor. */
  const showReverseChargeMessage = useMemo(
    () =>
      isGstEnabled &&
      shouldShowReverseChargeMessage(
        billData.gst_treatment,
        billData.gst_category,
        lineItems,
      ),
    [isGstEnabled, billData.gst_treatment, billData.gst_category, lineItems],
  );

  const reverseChargeAmount = useMemo(
    () =>
      resolveReverseChargeAmount({
        taxTotal: taxBreakdown.totalTax,
        documentCurrencyCode: billData.currency_code,
        documentCurrencySymbol: billData.currency_symbol,
        exchangeRate: parseExchangeRateInputForPayload(exchangeRateInput),
      }),
    [
      taxBreakdown.totalTax,
      billData.currency_code,
      billData.currency_symbol,
      exchangeRateInput,
    ],
  );

  const billTotals = useMemo(() => {
    const subtotal = lineItems.reduce(
      (sum, item) => sum + (item.rate ?? 0) * (item.quantity ?? 0),
      0,
    );
    const totalGst = isGstEnabled ? taxBreakdown.totalTax : 0;
    // Under reverse charge the GST is owed to the government, not the vendor,
    // so it leaves both the payable and the TCS base.
    const payableGst = showReverseChargeMessage ? 0 : totalGst;
    const baseForTcs = subtotal + discount + payableGst;
    const tcsPct = billData.tcs_tax_percentage ?? 0;
    const tcsAmount =
      billData.tcs_tax_id && tcsPct > 0 ? baseForTcs * (tcsPct / 100) : 0;
    const totalAmount = baseForTcs + tcsAmount;

    return {
      subtotal,
      tcsAmount,
      totalAmount,
      amountPayable: totalAmount - totalTDS,
    };
  }, [
    lineItems,
    isGstEnabled,
    taxBreakdown.totalTax,
    showReverseChargeMessage,
    discount,
    billData.tcs_tax_id,
    billData.tcs_tax_percentage,
    totalTDS,
  ]);

  const fetchTcsItems = useCallback(
    async (search: string, limit: number, offset: number) => {
      const res = await fetchTcsList(search, limit, offset);
      const items = res.data.tcs.map(mapTcsDTOToEntity);
      if (offset === 0) {
        return {
          items: [NO_TCS_ENTITY, ...items],
          hasMore: res.data.pagination.has_more,
        };
      }
      return { items, hasMore: res.data.pagination.has_more };
    },
    [],
  );

  const handleTcsSelect = useCallback(
    (item: TcsEntity) => {
      if (item.tcs_id === "") {
        updateBillData({
          tcs_tax_id: "",
          tcs_tax_name: "",
          tcs_tax_percentage: undefined,
          tcs_nature_of_collection: "",
        });
      } else {
        updateBillData({
          tcs_tax_id: item.tcs_id,
          tcs_tax_name: item.tcs_tax_name,
          tcs_tax_percentage: item.tcs_tax_percentage,
          tcs_nature_of_collection: item.tcs_nature_of_collection ?? "",
        });
      }
    },
    [updateBillData],
  );

  const setLineItems = useCallback((items: LineItemEntity[]) => {
    const withOrder = items.map((item, i) => ({ ...item, item_order: i + 1 }));
    const subTotal = withOrder.reduce((sum, i) => sum + (i.item_total ?? 0), 0);
    const taxTotal = withOrder.reduce((sum, i) => sum + (i.tax_amount ?? 0), 0);
    setBillData((prev) => ({
      ...prev,
      line_items: withOrder,
      sub_total: subTotal,
      tax_total: taxTotal,
      total: subTotal + taxTotal - (prev.discount ?? 0),
    }));
  }, []);

  const clearError = useCallback((field: string) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const findCurrencyById = useCallback(
    (currencyId: string) =>
      currencies.find(
        (c) => String(c.currency_id).trim() === currencyId.trim(),
      ),
    [currencies],
  );

  const handleCurrencyDropdownSelect = useCallback(
    (c: Currency) => {
      updateBillData({
        currency_id: c.currency_id,
        currency_code: c.currency_code as BillDataEntity["currency_code"],
        currency_symbol: c.currency_symbol,
      });
      setExchangeRateInput("");
      clearError("currency");
    },
    [updateBillData, clearError],
  );

  const handleVendorSelect = useCallback(
    async (vendor: VendorLikeForManualBill) => {
      // const vendorId = String(
      //   vendor.zoho_vendorid ?? vendor.vendorid ?? vendor.vendor_id ?? "",
      // ).trim();
      let enriched: VendorLikeForManualBill = { ...vendor };
      const destState = getStateAbbreviation(company?.state || "") || "";
      const patch = buildManualBillVendorPatch(enriched, destState, currencies);
      updateBillData(patch);
      setExchangeRateInput("");
      clearError("vendor");
    },
    [updateBillData, clearError, company?.state, currencies],
  );

  useEffect(() => {
    const st = location.state as CreateManualBillNavigationState | null;
    if (!st || (st.billDraft == null && st.applyVendorPatch == null)) {
      return;
    }
    setBillData((prev) => {
      const base = st.billDraft ?? prev;
      return { ...base, ...(st.applyVendorPatch || {}) };
    });
    if (st.applyVendorPatch) clearError("vendor");
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, clearError]);

  const handleAddLineItem = useCallback(() => {
    const newItem: LineItemEntity = {
      name: "",
      description: "",
      quantity: 0,
      rate: 0,
      unit: "nos",
      hsn_or_sac: "",
      hsn_description: "",
      sku: "",
      tax_id: "",
      tax_name: "",
      tax_percentage: 0,
      tax_amount: 0,
      account_id: "",
      account_name: "",
      item_total: 0,
      item_total_inclusive_of_tax: 0,
      item_order: lineItems.length + 1,
      is_billable: true,
    };
    setEditingLineItemIndex(-1);
    setEditingLineItemData(newItem);
    setOriginalAccountName("");
    setIsEditLineItemModalOpen(true);
  }, [lineItems.length]);

  const handleEditLineItem = useCallback(
    (index: number) => {
      setEditingLineItemIndex(index);
      const item = { ...lineItems[index] };
      if (item.tax_percentage === undefined || item.tax_percentage === null)
        item.tax_percentage = 0;
      setOriginalAccountName(item.account_name || "");
      setEditingLineItemData(item);
      setIsEditLineItemModalOpen(true);
    },
    [lineItems],
  );

  const handleDeleteLineItem = useCallback(
    (index: number) => {
      setLineItems(lineItems.filter((_, i) => i !== index));
    },
    [lineItems, setLineItems],
  );

  const handleSaveLineItem = useCallback(() => {
    if (!editingLineItemData) return;
    if (editingLineItemIndex === -1) {
      setLineItems([...lineItems, editingLineItemData]);
    } else if (editingLineItemIndex !== null) {
      const updated = [...lineItems];
      updated[editingLineItemIndex] = editingLineItemData;
      setLineItems(updated);
    }
    setIsEditLineItemModalOpen(false);
    setEditingLineItemData(null);
    setEditingLineItemIndex(null);
  }, [editingLineItemData, editingLineItemIndex, lineItems, setLineItems]);

  const handleLineItemDataChange = useCallback((data: LineItemEntity) => {
    setEditingLineItemData(data);
  }, []);

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      const valid: File[] = [];
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE_BYTES) continue;
        if (attachments.length + valid.length >= MAX_ATTACHMENTS) break;
        valid.push(file);
      }
      setAttachments((prev) => [...prev, ...valid].slice(0, MAX_ATTACHMENTS));
      e.target.value = "";
    },
    [attachments.length],
  );

  const handleRemoveFile = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const validate = useCallback((): boolean => {
    const next: Record<string, string> = {};
    if (!billData.vendor_id?.trim()) next.vendor = "Please select a vendor.";
    if (!billData.bill_number?.trim())
      next.bill_number = "Bill number is required.";
    if (!billData.currency_id?.trim())
      next.currency = "Please select a currency.";
    if (
      exchangeRateRequired &&
      isExchangeRateMissingForTallyPost(
        parseExchangeRateInputForPayload(exchangeRateInput),
        // A rate typed on this form is always deliberate, including a literal 1.
        { userSet: true, isPosted: false },
      )
    ) {
      next.exchange_rate =
        "Exchange rate is required for foreign currency documents when posting to Tally.";
    }
    if (!lineItems.length)
      next.line_items = "At least one line item is required.";
    if (
      isGstEnabled &&
      lineItems.some((i) => {
        const tid = String(i.tax_id ?? "").trim();
        const hasValidTax =
          tid.length > 0 || i.tax_id === "NO_TAX" || tid === "";
        return !hasValidTax;
      })
    ) {
      next.lineItemTax =
        "All line items must have a tax (or NO TAX) when GST is enabled.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [
    billData.vendor_id,
    billData.bill_number,
    billData.currency_id,
    exchangeRateRequired,
    exchangeRateInput,
    lineItems,
    isGstEnabled,
  ]);

  const handleSubmit = useCallback(async () => {
    setHasAttemptedSubmit(true);
    if (exchangeRateLoading) {
      showError("Wait for the exchange rate to finish loading.");
      return;
    }
    if (!validate()) {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (!canCreate) {
      showError("You do not have permission to create bills.");
      return;
    }
    setIsSubmitting(true);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.api;
      return next;
    });
    try {
      const built = buildManualBillJson(billData, lineItems, {
        currencies,
        companyGstEnabled: Number(companyGstEnabled) || 0,
        exchangeRateInput,
        // Zoho fills in the latest rate server-side; Tally has none to fall back on.
        requireExchangeRate: isTallyBackend,
      });
      if (!built.success) {
        setErrors((prev) => ({ ...prev, ...built.errors }));
        topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        setIsSubmitting(false);
        return;
      }
      const payload = built.data;

      if (requiresReviewForCreate) {
        const draftRes = await createManualBillDraft(
          payload,
          Number(companyGstEnabled) || 0,
          attachments.length > 0 ? attachments : undefined,
        );
        const draftId = draftRes?.data?.id;
        if (!draftId) {
          throw new Error("Failed to create bill draft");
        }
        setDraftIdForReview(draftId);
        setChangeDetailsForReview({
          raw_json: payload,
          action_types: ["create"],
        });
        setReviewModalOpen(true);
        setIsSubmitting(false);
        return;
      }
      const result = await createManualBillUseCase.execute(
        payload,
        Number(companyGstEnabled) || 0,
        attachments.length > 0 ? attachments : undefined,
      );
      showSuccess("Bill created successfully.");
      navigate("/bills/review", {
        state: { documentId: String(result.id), isEdit: true },
      });
    } catch (err: any) {
      const message = extractErrorMessage(err) || "Failed to create bill.";
      setErrors((prev) => ({ ...prev, api: message }));
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      showError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    canCreate,
    requiresReviewForCreate,
    billData,
    lineItems,
    currencies,
    exchangeRateInput,
    exchangeRateLoading,
    isTallyBackend,
    attachments,
    companyGstEnabled,
    validate,
    showError,
    showSuccess,
    navigate,
  ]);

  const handleReviewModalSuccess = useCallback(() => {
    setReviewModalOpen(false);
    setDraftIdForReview(null);
    setChangeDetailsForReview(null);
    navigate("/dashboard/bills");
  }, [navigate]);

  if (permissionsLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center">
          <span className="text-body font-body text-subtext-color">
            Loading...
          </span>
        </div>
      </DefaultPageLayout>
    );
  }

  if (!canCreate) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center">
          <span className="text-body font-body text-subtext-color">
            You do not have permission to create bills.
          </span>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div
        ref={topRef}
        className="mx-auto flex w-full max-w-7xl flex-col gap-8 bg-default-background px-6 py-6"
      >
        <Breadcrumbs>
          <Breadcrumbs.Item onClick={() => navigate("/dashboard")}>
            Dashboard
          </Breadcrumbs.Item>
          <Breadcrumbs.Divider />
          <Breadcrumbs.Item onClick={() => navigate("/dashboard")}>
            Bills
          </Breadcrumbs.Item>
          <Breadcrumbs.Divider />
          <Breadcrumbs.Item active>Create manual bill</Breadcrumbs.Item>
        </Breadcrumbs>

        <form
          className="flex w-full flex-col gap-8"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {hasAttemptedSubmit && Object.keys(errors).length > 0 && (
            <ErrorBanner
              errors={errors}
              title="Please fix the following errors:"
              autoScrollOnMount={true}
              context="bill"
            />
          )}
          <div className="flex w-full flex-col gap-6 rounded-lg border border-neutral-border bg-neutral-50 p-6">
            <h2 className="text-heading-2 font-heading-2 text-default-font">
              Bill details
            </h2>
            <div className="grid w-full grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="flex flex-col gap-3 sm:col-span-2">
                <div className="flex w-full items-center justify-between gap-4">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Vendor <span className="text-error-600">*</span>
                  </span>
                  <Button
                    type="button"
                    size="large"
                    icon={<FeatherUserPlus />}
                    onClick={() => setIsSearchPartyModalOpen(true)}
                  >
                    Search Party
                  </Button>
                </div>
                <div className="w-full items-start gap-4 grid grid-cols-1 sm:grid-cols-2">
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <span className="text-body font-body text-subtext-color">
                      Vendor name
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {vendorNameDisplayText}
                    </span>
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <span className="text-body font-body text-subtext-color">
                      Vendor address
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font break-words">
                      {vendorAddressDisplayText}
                    </span>
                  </div>
                </div>
                {errors.vendor && (
                  <span className="text-caption font-caption text-error-600">
                    {errors.vendor}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <TextField
                  label={
                    <>
                      Bill number <span className="text-error-600">*</span>
                    </>
                  }
                >
                  <TextField.Input
                    placeholder=""
                    value={billData.bill_number || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      updateBillData({ bill_number: e.target.value });
                      if (errors.bill_number) clearError("bill_number");
                    }}
                  />
                </TextField>
                {errors.bill_number ? (
                  <span className="text-caption font-caption text-error-600">
                    {errors.bill_number}
                  </span>
                ) : (
                  (() => {
                    const warning = getDocumentNumberValidationWarning(
                      billData.bill_number,
                      "Bill number",
                    );
                    return warning ? (
                      <span className="text-caption font-caption text-warning-700">
                        {warning}
                      </span>
                    ) : null;
                  })()
                )}
              </div>
              <div className="flex flex-col gap-2">
                <TextField label="Reference number">
                  <TextField.Input
                    placeholder=""
                    value={billData.reference_number || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateBillData({ reference_number: e.target.value })
                    }
                  />
                </TextField>
              </div>
              <div className="flex flex-col gap-2 sm:col-span-2">
                <div className="flex flex-row flex-wrap items-start gap-4 sm:gap-6">
                  <div className="flex min-w-[min(100%,12rem)] flex-1 flex-col gap-1.5">
                    <span className="text-caption-bold font-caption-bold text-default-font">
                      Currency <span className="text-error-600">*</span>
                    </span>
                    <SearchableDropdown<Currency>
                      items={currencies}
                      loading={currenciesLoading}
                      trigger={
                        <Button
                          type="button"
                          variant="neutral-secondary"
                          className="w-full max-w-full justify-between px-3 py-0"
                          iconRight={<FeatherChevronDown />}
                          disabled={currenciesLoading}
                        >
                          {currenciesLoading
                            ? "Loading currencies…"
                            : billData.currency_id
                              ? findCurrencyById(billData.currency_id)
                                  ?.currency_name_formatted || "Select currency"
                              : "Select currency"}
                        </Button>
                      }
                      getSearchableText={(c) =>
                        c.currency_name_formatted || c.currency_name
                      }
                      renderItem={(c) =>
                        c.currency_name_formatted || c.currency_name
                      }
                      getItemKey={(c) => c.currency_id}
                      onSelect={handleCurrencyDropdownSelect}
                      selectedKey={billData.currency_id}
                      searchPlaceholder="Search currencies…"
                      emptyMessage="No currencies available"
                      noResultsMessage="No currencies match your search"
                    />
                    {currenciesError && (
                      <span className="text-caption font-caption text-error-600">
                        {currenciesError}
                      </span>
                    )}
                    {errors.currency && (
                      <span className="text-caption font-caption text-error-600">
                        {errors.currency}
                      </span>
                    )}
                  </div>
                  {showExchangeRateField ? (
                    <div className="flex min-w-[min(100%,10rem)] flex-1 flex-col gap-1.5">
                      <TextField
                        label={
                          exchangeRateRequired ? (
                            <>
                              Exchange rate{" "}
                              <span className="text-error-600">*</span>
                            </>
                          ) : (
                            "Exchange rate"
                          )
                        }
                        helpText={
                          exchangeRateLoading
                            ? "Fetching the Zoho Books rate for this document date…"
                            : exchangeRateRequired
                              ? "Required for Tally posting. Enter the rate relative to your company base currency."
                              : "Leave empty for latest rate."
                        }
                        className="w-full"
                      >
                        <TextField.Input
                          type="number"
                          step="any"
                          inputMode="decimal"
                          placeholder={
                            exchangeRateLoading
                              ? "Loading…"
                              : exchangeRateRequired
                                ? "Required"
                                : "Optional"
                          }
                          value={
                            exchangeRateLoading ? "" : exchangeRateInput
                          }
                          disabled={exchangeRateLoading}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setExchangeRateInput(e.target.value);
                            clearError("exchange_rate");
                          }}
                          onWheel={(e) => e.preventDefault()}
                        />
                      </TextField>
                      {(errors.exchange_rate || exchangeRateFetchError) && (
                        <span className="text-caption font-caption text-error-600">
                          {errors.exchange_rate || exchangeRateFetchError}
                        </span>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:col-span-2 sm:flex-row sm:gap-6">
                <TextField
                  label={
                    <>
                      Date <span className="text-error-600">*</span>
                    </>
                  }
                  className="min-w-0 flex-1 sm:min-w-[200px]"
                >
                  <TextField.Input
                    className="h-10 w-full"
                    type="date"
                    value={billData.date}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateBillData({
                        date: e.target.value,
                        invoice_date: e.target.value,
                      })
                    }
                  />
                </TextField>
                <TextField
                  label={
                    <>
                      Due date <span className="text-error-600">*</span>
                    </>
                  }
                  className="min-w-0 flex-1 sm:min-w-[200px]"
                >
                  <TextField.Input
                    className="h-10 w-full"
                    type="date"
                    value={billData.due_date}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateBillData({ due_date: e.target.value })
                    }
                  />
                </TextField>
              </div>
              {/* ZOUR-19: appears the moment the date is moved before the
                  registration date, so the tax columns vanishing is explained
                  as it happens rather than discovered later. */}
              {showPreGstNotice && (
                <div className="sm:col-span-2">
                  <GstPreRegistrationNotice
                    documentDate={billData.date || billData.invoice_date || ""}
                    documentLabel="bill"
                  />
                </div>
              )}
              {/* ZOUR-65: which of our GST registrations receives this bill.
                  Above the GSTIN because it is what fills it. Hidden entirely
                  on a single-registration company. */}
              {hasMultipleLocations && (
                <div className="sm:col-span-2">
                  <LocationPicker
                    locations={activeLocations}
                    value={billData.location_id ?? null}
                    onChange={handleLocationChange}
                  />
                </div>
              )}

              <TextField label="Customer GSTIN">
                <TextField.Input
                  className="h-10"
                  placeholder="From company profile"
                  value={billData.customer_gstin ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateBillData({ customer_gstin: e.target.value })
                  }
                />
              </TextField>
              {isGstEnabled && (
                <TextField label="Vendor GSTIN">
                  <TextField.Input
                    className="h-10"
                    placeholder="From vendor"
                    value={billData.vendor_gstin || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const v = e.target.value;
                      updateBillData({ vendor_gstin: v, gst_no: v });
                    }}
                  />
                </TextField>
              )}
              <div className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-caption-bold font-caption-bold text-default-font">
                  Notes
                </span>
                <textarea
                  className="min-h-[80px] w-full resize-y rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font outline-none focus:border-brand-primary"
                  placeholder=""
                  value={billData.notes || ""}
                  onChange={(e) => updateBillData({ notes: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="flex w-full flex-col gap-4 rounded-lg border border-neutral-border bg-neutral-50 p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-heading-2 font-heading-2 text-default-font">
                Line Items <span className="text-error-600">*</span>
              </h2>
              <Button
                type="button"
                variant="neutral-primary"
                size="small"
                icon={<FeatherPlus />}
                onClick={handleAddLineItem}
              >
                Add line item
              </Button>
            </div>

            {/* Non-blocking: the lines are already gone, so this reports what
                happened rather than asking for a decision. Dismissible because
                it is about the last action, not about the state of the form. */}
            {locationClearedNote && (
              <div className="flex w-full items-start justify-between gap-3 rounded-md border border-solid border-warning-200 bg-warning-50 px-4 py-3">
                <span className="text-body font-body text-warning-800">
                  {locationClearedNote}
                </span>
                <button
                  type="button"
                  className="shrink-0 cursor-pointer text-caption font-caption text-warning-800 underline"
                  onClick={() => setLocationClearedNote(null)}
                >
                  Dismiss
                </button>
              </div>
            )}

            {lineItems.length === 0 ? (
              <div className="rounded-md border border-dashed border-neutral-border bg-neutral-50 py-8 text-center">
                <p className="text-body font-body text-subtext-color">
                  No line items yet. Click "Add line item" to add one.
                </p>
              </div>
            ) : (
              <div className="flex w-full flex-col items-start rounded-md border border-solid border-neutral-border overflow-x-auto">
                <div className="flex items-center gap-2 sm:gap-4 border-b border-solid border-neutral-border bg-default-background px-2 sm:px-4 py-3 w-full text-caption-bold font-caption-bold text-subtext-color">
                  <div className="flex min-w-0 flex-1 min-w-[90px] sm:min-w-[110px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      Description
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-1 min-w-[90px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      Debit A/c
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-1 min-w-[90px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      Credit A/c
                    </span>
                  </div>
                  <div className="flex min-w-0 w-[60px] sm:w-[80px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      HSN Code
                    </span>
                  </div>
                  <div className="flex min-w-0 w-[40px] sm:w-[64px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      Qty
                    </span>
                  </div>
                  <div className="flex min-w-0 w-[60px] sm:w-[80px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      Rate
                    </span>
                  </div>
                  {isGstEnabled && (
                    <div className="flex min-w-0 w-[40px] sm:w-[64px] shrink-0 items-start">
                      <span className="text-body font-body text-default-font truncate block max-w-full">
                        GST
                      </span>
                    </div>
                  )}
                  <div className="flex min-w-0 w-[80px] sm:w-[100px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      TDS
                    </span>
                  </div>
                  <div className="flex min-w-0 w-[60px] sm:w-[96px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      Amount
                    </span>
                  </div>
                  <div className="flex min-w-[120px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate">
                      Actions
                    </span>
                  </div>
                </div>
                {lineItems.map((item, index) => {
                  const tdsLabel =
                    item.tds_tax_name && (item.tds_tax_percentage ?? 0) > 0
                      ? `${item.tds_tax_name} (${item.tds_tax_percentage}%)`
                      : "NO TDS";
                  const gstLineLabel = formatDetectedLineItemGstDisplay(item);
                  return (
                    <div
                      key={`${item.name ?? ""}-${index}`}
                      className="flex items-start gap-2 sm:gap-4 border-b border-solid border-neutral-100 bg-white px-2 sm:px-4 py-3 w-full"
                    >
                      <div className="flex min-w-0 flex-1 min-w-[90px] sm:min-w-[110px] shrink-0 items-start">
                        <span
                          className="text-body-bold font-body-bold text-default-font truncate block max-w-full"
                          title={item.description || item.name || "—"}
                        >
                          {item.description || item.name || "—"}
                        </span>
                      </div>
                      <div className="flex min-w-0 flex-1 min-w-[90px] shrink-0 items-center">
                        <span
                          className="text-caption font-caption text-default-font truncate block max-w-full"
                          title={getAccountNameLeaf(item.account_name) || "—"}
                        >
                          {getAccountNameLeaf(item.account_name) || "—"}
                        </span>
                      </div>
                      <div className="flex min-w-0 flex-1 min-w-[90px] shrink-0 items-center">
                        <span
                          className="text-caption font-caption text-default-font truncate block max-w-full"
                          title="Accounts Payable"
                        >
                          Accounts Payable
                        </span>
                      </div>
                      <div className="flex min-w-0 w-[60px] sm:w-[80px] shrink-0 items-center">
                        <span
                          className="text-body font-body text-default-font truncate block max-w-full"
                          title={item.hsn_or_sac || "N/A"}
                        >
                          {item.hsn_or_sac || "N/A"}
                        </span>
                      </div>
                      <div className="flex min-w-0 w-[40px] sm:w-[64px] shrink-0 items-center">
                        <span
                          className="text-body font-body text-default-font truncate block max-w-full"
                          title={String(item.quantity ?? 0)}
                        >
                          {item.quantity ?? 0}
                        </span>
                      </div>
                      <div className="flex min-w-0 w-[60px] sm:w-[80px] shrink-0 items-center">
                        <span
                          className="text-body font-body text-default-font truncate block max-w-full"
                          title={`${billData.currency_symbol || "₹"}${formatAmount(item.rate ?? 0)}`}
                        >
                          {billData.currency_symbol || "₹"}
                          {formatAmount(item.rate ?? 0)}
                        </span>
                      </div>
                      {isGstEnabled && (
                        <div className="flex min-w-0 w-[40px] sm:w-[64px] shrink-0 items-center">
                          <span
                            className="text-body font-body text-default-font truncate block max-w-full"
                            title={gstLineLabel}
                          >
                            {gstLineLabel}
                          </span>
                        </div>
                      )}
                      <div className="flex min-w-0 w-[80px] sm:w-[100px] shrink-0 items-center">
                        <span
                          className="text-body font-body text-default-font truncate block max-w-full"
                          title={tdsLabel}
                        >
                          {tdsLabel}
                        </span>
                      </div>
                      <div className="flex min-w-0 w-[60px] sm:w-[96px] shrink-0 items-center">
                        <span
                          className="text-body-bold font-body-bold text-default-font truncate block max-w-full"
                          title={`${billData.currency_symbol || "₹"}${formatAmount(
                            isGstEnabled
                              ? (item.rate ?? 0) * (item.quantity ?? 0) +
                                  ((item.rate ?? 0) *
                                    (item.quantity ?? 0) *
                                    (item.tax_percentage ?? 0)) /
                                    100
                              : (item.rate ?? 0) * (item.quantity ?? 0),
                          )}`}
                        >
                          {billData.currency_symbol || "₹"}
                          {formatAmount(
                            isGstEnabled
                              ? (item.rate ?? 0) * (item.quantity ?? 0) +
                                  ((item.rate ?? 0) *
                                    (item.quantity ?? 0) *
                                    (item.tax_percentage ?? 0)) /
                                    100
                              : (item.rate ?? 0) * (item.quantity ?? 0),
                          )}
                        </span>
                      </div>
                      <div className="flex min-w-[120px] shrink-0 items-center gap-2">
                        <Button
                          type="button"
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherEdit2 />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditLineItem(index);
                          }}
                        >
                          Edit
                        </Button>
                        <IconButton
                          size="small"
                          icon={<FeatherX />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLineItem(index);
                          }}
                          aria-label="Remove line"
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="flex flex-col items-end gap-3 border-t border-solid border-neutral-200 bg-default-background px-4 py-4 min-w-full">
                  <div className="flex w-full max-w-[384px] items-center justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Subtotal
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {billData.currency_symbol || "₹"}
                      {formatAmount(billTotals.subtotal)}
                    </span>
                  </div>
                  {discount !== 0 && (
                    <div className="flex w-full max-w-[384px] items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Discount/Adjustments
                      </span>
                      <span
                        className={`text-body-bold font-body-bold ${
                          discount < 0 ? "text-success-600" : "text-error-600"
                        }`}
                      >
                        {billData.currency_symbol || "₹"}
                        {formatAmount(discount)}
                      </span>
                    </div>
                  )}
                  {isGstEnabled &&
                    !showReverseChargeMessage &&
                    taxBreakdown.rows.map((row) => (
                      <div
                        key={row.label}
                        className="flex w-full max-w-[384px] items-center justify-between"
                      >
                        <span className="text-body font-body text-subtext-color">
                          {row.label}
                        </span>
                        <span className="text-body-bold font-body-bold text-default-font">
                          {billData.currency_symbol || "₹"}
                          {formatAmount(row.amount)}
                        </span>
                      </div>
                    ))}
                  {isGstEnabled && (
                    <>
                      {hasAnyTds ? (
                        <div className="flex w-full max-w-[384px] flex-col items-end gap-0.5">
                          <div className="flex w-full items-center justify-between">
                            <span className="text-body font-body text-subtext-color">
                              TCS
                            </span>
                            <span className="text-body-bold font-body-bold text-default-font">
                              No TCS
                            </span>
                          </div>
                          <span className="text-body font-body text-subtext-color w-full text-right text-sm">
                            TDS is selected so TCS cannot be applied.
                          </span>
                        </div>
                      ) : (
                        <div className="flex w-full max-w-[384px] items-center justify-between">
                          <span className="text-body font-body text-subtext-color">
                            TCS
                          </span>
                          <div className="w-full max-w-[220px] min-w-0 flex justify-end">
                            <SearchableDropdown<TcsEntity>
                              fetchItems={fetchTcsItems}
                              initialLimit={10}
                              maxHeight="max-h-60"
                              className="w-full"
                              dropdownClassName="min-w-[200px]"
                              trigger={
                                <Button
                                  variant="neutral-secondary"
                                  size="small"
                                  iconRight={<FeatherChevronDown />}
                                  className="w-full min-h-10 h-10 py-2 min-w-0 justify-between"
                                >
                                  <span className="truncate min-w-0">
                                    {billData.tcs_tax_name &&
                                    billData.tcs_tax_id
                                      ? billData.tcs_tax_name
                                      : "No TCS"}
                                  </span>
                                </Button>
                              }
                              placeholder="Select TCS"
                              getSearchableText={(item) => item.tcs_tax_name}
                              renderItem={(item) =>
                                item.tcs_id === ""
                                  ? item.tcs_tax_name
                                  : `${item.tcs_tax_name} (${item.tcs_tax_percentage}%)`
                              }
                              getItemKey={(item) => item.tcs_id}
                              onSelect={handleTcsSelect}
                              selectedKey={billData.tcs_tax_id ?? ""}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {isGstEnabled && billTotals.tcsAmount > 0 && (
                    <div className="flex w-full max-w-[384px] items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        TCS
                        {billData.tcs_tax_name
                          ? ` (${billData.tcs_tax_name})`
                          : ""}
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {billData.currency_symbol || "₹"}
                        {formatAmount(billTotals.tcsAmount)}
                      </span>
                    </div>
                  )}
                  <div className="flex w-full max-w-[384px] items-center justify-between border-t border-solid border-neutral-border pt-3">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Total Amount
                    </span>
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      {billData.currency_symbol || "₹"}
                      {formatAmount(billTotals.totalAmount)}
                    </span>
                  </div>
                  {totalTDS > 0 && (
                    <>
                      <div className="flex w-full max-w-[384px] items-center justify-between">
                        <span className="text-body font-body text-subtext-color">
                          TDS Deducted
                        </span>
                        <span className="text-body-bold font-body-bold text-error-600">
                          -{billData.currency_symbol || "₹"}
                          {formatAmount(totalTDS)}
                        </span>
                      </div>
                      <div className="flex w-full max-w-[384px] items-center justify-between">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Amount Payable
                        </span>
                        <span className="text-heading-3 font-heading-3 text-default-font">
                          {billData.currency_symbol || "₹"}
                          {formatAmount(billTotals.amountPayable)}
                        </span>
                      </div>
                    </>
                  )}
                  {showReverseChargeMessage && (
                    <>
                      {taxBreakdown.rows.map((row) => (
                        <div
                          key={row.label}
                          className="flex w-full max-w-[384px] items-center justify-between"
                        >
                          <span className="text-body font-body text-subtext-color">
                            {row.label}
                          </span>
                          <span className="text-body-bold font-body-bold text-default-font">
                            {billData.currency_symbol || "₹"}
                            {formatAmount(row.amount)}
                          </span>
                        </div>
                      ))}
                      <div className="flex w-full max-w-[384px] items-start gap-2 rounded-md border border-solid border-info-200 bg-info-50 px-3 py-2">
                        <FeatherInfo className="h-4 w-4 shrink-0 text-info-700" />
                        <span className="text-body font-body text-info-700">
                          Reverse charges of{" "}
                          {formatReverseChargeAmountWithDocument(
                            reverseChargeAmount,
                          )}{" "}
                          will be applied
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex w-full flex-col gap-4 rounded-lg border border-neutral-border bg-neutral-50 p-6">
            <h2 className="text-heading-3 font-heading-3 text-default-font">
              Attachments
            </h2>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="file"
                id="manual-bill-file-upload"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept="*"
              />
              <Button
                type="button"
                variant="neutral-secondary"
                iconRight={<FeatherPlus />}
                disabled={attachments.length >= MAX_ATTACHMENTS}
                onClick={() =>
                  document.getElementById("manual-bill-file-upload")?.click()
                }
              >
                Upload File
              </Button>
              <span className="text-caption font-caption text-subtext-color">
                Max {MAX_ATTACHMENTS} files, {MAX_FILE_SIZE_MB}MB each (
                {attachments.length}/{MAX_ATTACHMENTS})
              </span>
            </div>
            {attachments.length === 0 ? (
              <div className="rounded-md border border-dashed border-neutral-border bg-neutral-50 py-6 text-center">
                <span className="text-body font-body text-subtext-color">
                  No attachments
                </span>
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {attachments.map((file, index) => (
                  <li
                    key={index}
                    className="flex items-center justify-between rounded bg-white px-3 py-2 border border-neutral-100"
                  >
                    <span className="truncate text-body font-body text-default-font">
                      {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                    <IconButton
                      size="small"
                      icon={<FeatherX />}
                      onClick={() => handleRemoveFile(index)}
                      aria-label="Remove file"
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex w-full justify-end gap-3 border-t border-neutral-border pt-6">
            <Button
              type="button"
              variant="neutral-tertiary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              disabled={isSubmitting || exchangeRateLoading}
            >
              {isSubmitting
                ? requiresReviewForCreate
                  ? "Creating draft…"
                  : "Creating…"
                : exchangeRateLoading
                  ? "Loading exchange rate…"
                  : requiresReviewForCreate
                    ? "Submit for review"
                    : createAndPushLabel}
            </Button>
          </div>
        </form>

        <ReviewRequestModal
          open={reviewModalOpen}
          onOpenChange={setReviewModalOpen}
          moduleCode={MODULE_CODES.BILL}
          actionCode={ACTION_CODES.CREATE}
          entityType="bill"
          entityId={draftIdForReview ?? 0}
          changeDetails={changeDetailsForReview ?? undefined}
          onSuccess={handleReviewModalSuccess}
          skipNavigation={true}
          title="Submit for review"
        />

        <SearchPartyModal
          open={isSearchPartyModalOpen}
          onOpenChange={setIsSearchPartyModalOpen}
          onPartySelect={(party) => {
            void handleVendorSelect(party as VendorLikeForManualBill);
          }}
          initialSearchTerm={billData.vendor_name}
          initialSelectedVendorId={billData.vendor_id || undefined}
          createManualBillFlow={{
            returnPath: CREATE_MANUAL_BILL_ROUTE,
            billDraft: billData,
          }}
        />
      </div>

      <EditLineItemManualBill
        open={isEditLineItemModalOpen}
        onOpenChange={setIsEditLineItemModalOpen}
        editingLineItemData={editingLineItemData}
        originalAccountName={originalAccountName}
        editingLineItemIndex={editingLineItemIndex}
        taxes={taxes}
        vendorId={billData.vendor_id}
        vendorName={billData.vendor_name}
        vendorGstTreatment={billData.gst_treatment}
        vendorGstCategory={billData.gst_category}
        currencyCode={billData.currency_code}
        exchangeRate={parseExchangeRateInputForPayload(exchangeRateInput)}
        isNewItem={editingLineItemIndex === -1}
        isGstEnabled={isGstEnabled}
        currencySymbol={billData.currency_symbol}
        isTdsApplicable={true}
        isTcsApplied={!!(billData.tcs_tax_id && billData.tcs_tax_id !== "")}
        isInterState={isInterState}
        documentDate={billData.date || billData.invoice_date || ""}
        onSave={handleSaveLineItem}
        onCancel={() => {
          setIsEditLineItemModalOpen(false);
          setEditingLineItemData(null);
          setEditingLineItemIndex(null);
        }}
        onLineItemDataChange={handleLineItemDataChange}
      />
    </DefaultPageLayout>
  );
};

export default CreateManualBillPage;
